/**
 * Variation Generator Algorithm
 *
 * Pure utility for generating weighted-random attribute variations
 * from a DataTable's categories/options. No React dependencies.
 */

// ── Types ───────────────────────────────────────────────────────────────────

export interface AttributeOption {
  id: string
  name: string
  weight: number
  promptFragment: string
}

export interface AttributeCategory {
  id: string
  name: string
  sortOrder: number
  isRequired: boolean
  noneWeight: number
  options: AttributeOption[]
}

export interface ConflictRule {
  id: string
  enabled: boolean
  condition: { categoryId: string; optionId: string }
  exclusion: { categoryId: string; optionId: string }
}

export interface SelectedAttribute {
  categoryId: string
  categoryName: string
  optionId: string | null
  optionName: string | null
  promptFragment: string | null
}

export interface GeneratedVariation {
  index: number
  attributes: SelectedAttribute[]
  hash: string
  locked: boolean
  status: 'generated' | 'manual'
}

export interface GenerationStats {
  requested: number
  generated: number
  duplicatesSkipped: number
  conflictsResolved: number
  durationMs: number
  maxPossible: number
  maxDeviation: number  // max % deviation from target weights across all categories
}

export interface GenerationResult {
  variations: GeneratedVariation[]
  stats: GenerationStats
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Seeded pseudo-random number generator (mulberry32) */
function createSeededRandom(seed: number): () => number {
  let s = seed | 0
  return () => {
    s = (s + 0x6d2b79f5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Simple string hash (djb2) for duplicate detection */
function hashString(str: string): string {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0
  }
  return (hash >>> 0).toString(36)
}

function hashVariation(attributes: SelectedAttribute[]): string {
  const key = attributes
    .map((a) => `${a.categoryId}:${a.optionId ?? 'none'}`)
    .join('|')
  return hashString(key)
}

/** Weighted random selection. Returns null if "none" is selected. */
function weightedSelect(
  options: AttributeOption[],
  noneWeight: number,
  random: () => number = Math.random,
): AttributeOption | null {
  const totalOptionWeight = options.reduce((s, o) => s + o.weight, 0)
  const totalWeight = totalOptionWeight + noneWeight

  if (totalWeight <= 0) return options.length > 0 ? options[0] : null

  let roll = random() * totalWeight

  // Check "none" first
  if (noneWeight > 0) {
    roll -= noneWeight
    if (roll < 0) return null
  }

  for (const opt of options) {
    roll -= opt.weight
    if (roll < 0) return opt
  }

  // Fallback (rounding edge case)
  return options[options.length - 1] ?? null
}

/** Check if a partial variation violates any enabled conflict rules */
function hasConflict(
  attributes: SelectedAttribute[],
  rules: ConflictRule[],
): boolean {
  for (const rule of rules) {
    if (!rule.enabled) continue

    const conditionMet = attributes.some(
      (a) =>
        a.categoryId === rule.condition.categoryId &&
        a.optionId === rule.condition.optionId,
    )
    if (!conditionMet) continue

    const exclusionViolated = attributes.some(
      (a) =>
        a.categoryId === rule.exclusion.categoryId &&
        a.optionId === rule.exclusion.optionId,
    )
    if (exclusionViolated) return true
  }
  return false
}

/** Estimate max unique combinations (excluding conflicts) */
function estimateMaxCombinations(categories: AttributeCategory[]): number {
  let max = 1
  for (const cat of categories) {
    const optionCount = cat.options.length + (cat.noneWeight > 0 ? 1 : 0)
    max *= Math.max(optionCount, 1)
    // Cap at a large number to avoid overflow
    if (max > 1_000_000) return 1_000_000
  }
  return max
}

// ── Main Generator ──────────────────────────────────────────────────────────

/** Generate a single variation */
function generateSingleVariation(
  categories: AttributeCategory[],
  rules: ConflictRule[],
  random: () => number = Math.random,
): { variation: SelectedAttribute[]; conflictsResolved: number } {
  const MAX_CONFLICT_RETRIES = 10
  let conflictsResolved = 0
  const attributes: SelectedAttribute[] = []

  for (const cat of categories) {
    let selected: AttributeOption | null = null
    let attempts = 0

    while (attempts < MAX_CONFLICT_RETRIES) {
      selected = weightedSelect(cat.options, cat.isRequired ? 0 : cat.noneWeight, random)

      const candidate: SelectedAttribute = {
        categoryId: cat.id,
        categoryName: cat.name,
        optionId: selected?.id ?? null,
        optionName: selected?.name ?? null,
        promptFragment: selected?.promptFragment ?? null,
      }

      // Check conflict with attributes already chosen in this variation
      const testAttributes = [...attributes, candidate]
      if (!hasConflict(testAttributes, rules)) {
        attributes.push(candidate)
        break
      }

      conflictsResolved++
      attempts++
    }

    // If all retries exhausted, force any non-conflicting option
    if (attempts >= MAX_CONFLICT_RETRIES) {
      const fallback = cat.options.find((opt) => {
        const candidate: SelectedAttribute = {
          categoryId: cat.id,
          categoryName: cat.name,
          optionId: opt.id,
          optionName: opt.name,
          promptFragment: opt.promptFragment,
        }
        return !hasConflict([...attributes, candidate], rules)
      })

      attributes.push({
        categoryId: cat.id,
        categoryName: cat.name,
        optionId: fallback?.id ?? null,
        optionName: fallback?.name ?? null,
        promptFragment: fallback?.promptFragment ?? null,
      })
    }
  }

  return { variation: attributes, conflictsResolved }
}

/**
 * Generate N variations asynchronously with progress reporting.
 * Yields to the event loop every CHUNK_SIZE iterations.
 */
export async function generateVariationsAsync(
  categories: AttributeCategory[],
  count: number,
  conflictRules: ConflictRule[] = [],
  onProgress?: (generated: number, total: number) => void,
  seed?: number,
): Promise<GenerationResult> {
  const CHUNK_SIZE = 500
  const MAX_DUPE_RETRIES = 50
  const startTime = performance.now()

  const random = seed !== undefined ? createSeededRandom(seed) : Math.random

  // Sort categories by sortOrder
  const sorted = [...categories].sort((a, b) => a.sortOrder - b.sortOrder)
  const enabledRules = conflictRules.filter((r) => r.enabled)

  const maxPossible = estimateMaxCombinations(sorted)
  const targetCount = Math.min(count, maxPossible)

  const variations: GeneratedVariation[] = []
  const seenHashes = new Set<string>()
  let duplicatesSkipped = 0
  let totalConflictsResolved = 0

  let generated = 0
  while (generated < targetCount) {
    // Process a chunk
    const chunkEnd = Math.min(generated + CHUNK_SIZE, targetCount)

    for (let i = generated; i < chunkEnd; i++) {
      let dupeAttempts = 0
      let added = false

      while (dupeAttempts < MAX_DUPE_RETRIES && !added) {
        const { variation, conflictsResolved } = generateSingleVariation(sorted, enabledRules, random)
        totalConflictsResolved += conflictsResolved

        const hash = hashVariation(variation)

        if (!seenHashes.has(hash)) {
          seenHashes.add(hash)
          variations.push({
            index: variations.length + 1,
            attributes: variation,
            hash,
            locked: false,
            status: 'generated',
          })
          added = true
        } else {
          duplicatesSkipped++
          dupeAttempts++
        }
      }

      // If we exhausted retries, the combinatorial space is saturated
      if (!added) {
        generated = targetCount // Break outer loop
        break
      }
    }

    generated = variations.length

    // Report progress
    onProgress?.(generated, targetCount)

    // Yield to event loop between chunks
    if (generated < targetCount) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0))
    }
  }

  const durationMs = Math.round(performance.now() - startTime)

  // Calculate max deviation from target weights
  let maxDeviation = 0
  if (variations.length > 0) {
    for (const cat of sorted) {
      const totalWeight = cat.options.reduce((s, o) => s + o.weight, 0) + (cat.isRequired ? 0 : cat.noneWeight)
      if (totalWeight <= 0) continue
      for (const opt of cat.options) {
        const targetPct = (opt.weight / totalWeight) * 100
        const actualCount = variations.filter((v) =>
          v.attributes.some((a) => a.categoryId === cat.id && a.optionId === opt.id)
        ).length
        const actualPct = (actualCount / variations.length) * 100
        const deviation = Math.abs(actualPct - targetPct)
        if (deviation > maxDeviation) maxDeviation = deviation
      }
    }
  }

  return {
    variations,
    stats: {
      requested: count,
      generated: variations.length,
      duplicatesSkipped,
      conflictsResolved: totalConflictsResolved,
      durationMs,
      maxPossible,
      maxDeviation: Math.round(maxDeviation * 10) / 10,
    },
  }
}
